import React, { useEffect, useState, useContext, useRef } from 'react';
import '../styles/Posts.css';
import { useNavigate } from 'react-router-dom';
import { formatTimeCompact } from '../utils/formatTimeCompact';
import { getHeartIcon, getCommentsIcon } from '../utils/getThemeIcon';
import { getAuthorIconUrl } from '../utils/getAuthorPlaceholder';
import { ThemeContext } from '../context/ThemeContext';

import { ApiContext } from '../context/ApiContext';
import apiCall from '../utils/apiCall';
import useAuth from '../hooks/useAuth';
import NewPost from './NewPost';
import useAuthState from '../hooks/useAuthState';
import { useDemo } from '../context/DemoContext';

const Posts = () => {

    const { isAuthenticated } = useAuthState();
    const navigate = useNavigate();
    const { callApi } = useAuth();
    const { isDemoMode, demoPosts, demoLikedPostIds, likePost: demoLikePost } = useDemo();

    const postRefs = useRef(null); // Define postRefs
    const scrollRestoredRef = useRef(false);
    const apiUrl = useContext(ApiContext);
    const { theme } = useContext(ThemeContext);
    const [posts, setPosts] = useState([]);
    const [likedPostIds, setLikedPostIds] = useState([]);
    const [likesTrigger, setLikesTrigger] = useState(false);
    const [expandedPosts, setExpandedPosts] = useState({});

    // Helper function to count lines in post content
    const countPostLines = (text) => {
        if (!text) return 0;
        return text.split('\n').length;
    };

    // Helper function to toggle post expansion
    const togglePostExpansion = (postId) => {
        setExpandedPosts(prev => ({
            ...prev,
            [postId]: !prev[postId]
        }));
    };

    const handleLikePost = async (postId) => {
        if (isDemoMode) {
            demoLikePost(postId);
            return;
        }
        try {
            const likeUrl = apiUrl ? `${apiUrl}/api/v1/post/like` : '/api/v1/post/like';
            const response = await apiCall(likeUrl, 'POST', { id: postId });
            if (response.ok) {
                setLikesTrigger(!likesTrigger);
            } else {
                console.error('Failed to like post');
            }
        } catch (error) {
            console.error('Error while liking post:', error);
        }
    };

    const handlePostClick = (post) => {
        if (postRefs.current) {
            sessionStorage.setItem('feedScrollPosition', postRefs.current.scrollTop);
        }
        navigate(`/posts/post/${post.id}`, { state: { initialPost: post } });
    };

   useEffect(() => {
       if (scrollRestoredRef.current || posts.length === 0) return;
       const scrollPosition = parseInt(sessionStorage.getItem('feedScrollPosition')) || 0;
       if (postRefs.current && scrollPosition > 0) {
           postRefs.current.scrollTop = scrollPosition;
           sessionStorage.removeItem('feedScrollPosition');
           scrollRestoredRef.current = true;
       }
   }, [posts]);

    useEffect(() => {
        const fetchPosts = async () => {
            if (isDemoMode) {
                setPosts(demoPosts);
                return;
            }
            try {
                const postListUrl = apiUrl ? `${apiUrl}/api/v1/post/list` : '/api/v1/post/list';
                const response = await apiCall(postListUrl, 'GET');
                if (response.ok) {
                    const data = await response.json();
                    setPosts(data);
                } else {
                    console.error('Failed to fetch posts');
                }
            } catch (error) {
                console.error('Error while fetching posts:', error);
            }
        };

        fetchPosts();
    }, [apiUrl, likesTrigger, isDemoMode, demoPosts]);

    useEffect(() => {
        const fetchLikedPosts = async () => {
            if (isDemoMode) {
                setLikedPostIds(demoLikedPostIds);
                return;
            }
            if (!isAuthenticated || posts.length === 0) {
                setLikedPostIds([]);
                return;
            }

            const likedUrl = apiUrl ? `${apiUrl}/api/v1/post/liked` : '/api/v1/post/liked';
            try {
                const likedResponse = await callApi(likedUrl, 
                    'POST',
                    { ids: posts.map((post) => post.id) },
                );
                if (likedResponse.ok) {
                    const likedData = await likedResponse.json();
                    setLikedPostIds(likedData.ids);
                } else {
                    if (likedResponse.status === 401) {
                        setLikedPostIds([]);
                    } else {
                        console.error('Failed to fetch liked posts');
                    }
                }
            } catch (error) {
                console.error('Error fetching liked posts:', error);
                setLikedPostIds([]);
            }
        }

        fetchLikedPosts();
    }, [apiUrl, posts]);
    return (
        <div className="posts">
            <div className="posts-scrollable" ref={postRefs}>
                <ul>
                {posts.map((post, index) => (
                    <li 
                        key={post.id} 
                        className="post-item"
                        onClick={() => {
                            console.log(`inserting post ${post.id}`);
                            handlePostClick(post);
                        }}
                    >
                        <img src={getAuthorIconUrl(post, apiUrl, theme)} alt="Author" className="post-author-icon" />
                        <div className="post-body">
                            <div className="post-header">
                                <p className="post-author"> @{post.authorUsername} </p>
                                <p className="post-date">{formatTimeCompact(post.createdAt)}</p>
                            </div>
                            {countPostLines(post.content) > 3 && !expandedPosts[post.id] ? (
                                <>
                                    <p className="post-content post-content-collapsed">
                                        {post.content.split('\n').slice(0, 3).join('\n')}
                                    </p>
                                    <div className="post-expand-button-container">
                                        <button
                                            className="post-expand-button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                togglePostExpansion(post.id);
                                            }}
                                        >
                                            ...show more
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <p className="post-content">
                                    {post.content}
                                </p>
                            )}
                            {expandedPosts[post.id] && countPostLines(post.content) > 3 && (
                                <div className="post-expand-button-container">
                                    <button
                                        className="post-expand-button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            togglePostExpansion(post.id);
                                        }}
                                    >
                                        show less
                                    </button>
                                </div>
                            )}
                            <div className="post-actions-container">
                                <div className="post-like-action">
                                    <img
                                        src={getHeartIcon(theme, likedPostIds.includes(post.id))}
                                        alt="Like"
                                        className="post-like-icon"
                                        onClick={(e) => {
                                            e.stopPropagation(); // Prevent triggering the parent onClick
                                            handleLikePost(post.id);
                                        }}
                                    />
                                    <span>{ post.likesCount }</span>
                                </div>
                                {post.commentsCount > 0 && (
                                    <div className="post-comment-action">
                                        <img
                                            className="post-like-icon"
                                            src={getCommentsIcon(theme)}
                                            alt="Comments"
                                        />
                                        <span>{post.commentsCount}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </li>
                ))}
            </ul>
            </div>
            <NewPost />
        </div>
    );
};

export default Posts;